// Blocked dates — the days and times this driver has said they are unavailable.
//
// Was a collapsed accordion with a text "+ Add blacklisted date" button above a 300px
// column of dates. Now a `SectionCard` with the add as the header's icon action, and one
// hairline `Row` per date with its delete on the trailing edge.
//
// "Blocked" rather than "blacklisted" in the title only. The redux actions and the API
// keep their names — renaming those is a backend change, not a restyle.
import React, { useState } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import {
  Stack,
  Box,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DatePicker,
  TimeField,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '../../../ui/core';
import { scrollY } from '../../../ui/styles';
import { DeleteIcon, CalendarIcon } from '../../../ui/icons';
import { useFormatters } from '../../../hooks';
import {
  addBlacklistedDate,
  addBlacklistedDateTime,
  removeBlacklistedDate,
  removeBlacklistedDateTime,
} from '../../../redux/Users/actions';
import { useHasPermission } from '../../../helpers/api/auth/index';
import { PERMISSIONS } from '../../../helpers/api/auth/permissions';
import { SectionCard, EmptyRows } from '../../../pages/DriverProfile/components';
import { Row, IconAction, TrailingMeta } from '../../../pages/UserProfile/parts';

const AddDialog = ({ user, onClose }) => {
  const [date, setDate] = useState(null);
  const [hasTime, setHasTime] = useState(false);
  const dispatch = useDispatch();

  // A timed block is a wall-clock time in the driver's own city, so it can only be turned
  // into an instant with that city's zone. Plenty of drivers have no resolved `homeCity`
  // (the admin `user` query $lookups it with `preserveNullAndEmptyArrays`), and
  // `moment.tz(str, undefined)` does not fail loudly — it reads the wall time as UTC and
  // stores a block hours away from the one that was typed. Whole-day blocks need no zone,
  // so those stay available; only the time half is withheld.
  const timeZone = user.homeCity?.timeZone;

  const isValid = date && date.isValid();

  const onConfirm = async () => {
    if (hasTime && timeZone) {
      const dateTime = moment.tz(date.format('YYYY-MM-DD HH:mm'), timeZone).format();
      const { success } = await addBlacklistedDateTime(user.id, dateTime)(dispatch);
      success && onClose();
    } else {
      const { success } = await addBlacklistedDate(user.id, date.format('YYYY-MM-DD'))(dispatch);
      success && onClose();
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent sx={{ paddingTop: '32px' }}>
        <Stack spacing="48px" width="400px">
          <Stack direction="row" spacing="8px" alignItems="center">
            <DatePicker fullWidth label="Date" value={date} moment onChange={(v) => setDate(v)} />
          </Stack>
          <Box ml="24px !important">
            <FormControlLabel
              control={
                <Switch
                  sx={{ transform: 'scale(2)', marginRight: '10px' }}
                  checked={hasTime && !!timeZone}
                  disabled={!timeZone}
                  onChange={(e) => setHasTime(e.target.checked)}
                  inputProps={{ 'aria-label': 'Add time' }}
                />
              }
              label={timeZone ? 'Add time' : 'Add time (needs a home city)'}
            />
          </Box>
          <Stack visibility={hasTime && timeZone ? 'visible' : 'hidden'} direction="row" spacing="8px" alignItems="center">
            <TimeField label="Time" value={date} fullWidth onChange={(v) => setDate(v)} />
          </Stack>
          <Stack direction="row" justifyContent="flex-end" spacing="16px">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" disabled={!isValid} onClick={onConfirm}>
              Confirm
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const BlockedDateItem = ({ user, value, hasTime, onDelete, waiting, first }) => {
  const f = useFormatters();
  const hasRemoveBlockDatePermission = useHasPermission('remove_block_date');
  return (
    <Row
      first={first}
      label={
        hasTime
          ? f.date.toTimezoneLocaleString(
              value,
              user.homeCity?.timeZone,
              f.date.DateFormats.DATETIME_SHORT_WITH_TIMEZONE,
            )
          : f.date.toLocaleString(value, f.date.DateFormats.DATE_MED)
      }
      // Which kind of block this is, said rather than implied by the format: a
      // whole-day block and a one-departure block are different answers to "why did
      // this driver not get offered that trip".
      meta={hasTime ? 'Time' : 'Whole day'}
      right={
        hasRemoveBlockDatePermission && (
          <IconButton
            color="error"
            size="small"
            aria-label="Remove this blocked date"
            title="Remove this blocked date"
            onClick={() => onDelete(value)}
            sx={{ width: 26, height: 26 }}
          >
            {waiting ? <CircularProgress size="15px" color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        )
      }
    />
  );
};

const DriverBlockDates = ({ user }) => {
  const dispatch = useDispatch();
  // VIEW_DRIVER may read this list. Adding a block is a driver-record mutation, so it
  // stays with MANAGE_DRIVERS; removal keeps its narrower explicit permission below.
  const canAddBlockedDate = useHasPermission(PERMISSIONS.MANAGE_DRIVERS);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [waitingFor, setWaitingFor] = useState(null);

  const openAddDialog = () => setShowAddDialog(true);
  const closeAddDialog = () => setShowAddDialog(false);

  const deleteBlockedDate = async (value) => {
    setWaitingFor(value);
    await removeBlacklistedDate(user.id, value)(dispatch);
    setWaitingFor(null);
  };

  const deleteBlockedDateTime = async (value) => {
    setWaitingFor(value);
    await removeBlacklistedDateTime(user.id, value)(dispatch);
    setWaitingFor(null);
  };

  const dates = user.blacklistedDates ?? [];
  const times = user.blacklistedTimes ?? [];

  return (
    <SectionCard
      title="Blocked dates"
      icon={<CalendarIcon sx={{ fontSize: 17, color: 'text.secondary', flexShrink: 0 }} />}
      meta={
        canAddBlockedDate ? (
          <TrailingMeta>
            <IconAction kind="add" label="Add a blocked date" onClick={openAddDialog} />
          </TrailingMeta>
        ) : null
      }
    >
      {showAddDialog && <AddDialog user={user} onClose={closeAddDialog} />}
      {dates.length === 0 && times.length === 0 ? (
        <EmptyRows>No blocked dates.</EmptyRows>
      ) : (
        <Box sx={{ maxHeight: 300, ...scrollY }}>
          {dates.map((date, i) => (
            <BlockedDateItem
              key={date}
              first={i === 0}
              user={user}
              value={date}
              waiting={waitingFor === date}
              onDelete={deleteBlockedDate}
            />
          ))}
          {times.map((time) => (
            <BlockedDateItem
              key={time}
              user={user}
              value={time}
              hasTime
              waiting={waitingFor === time}
              onDelete={deleteBlockedDateTime}
            />
          ))}
        </Box>
      )}
    </SectionCard>
  );
};

export default DriverBlockDates;
